import { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { CheckCircle, XCircle, Gift, Users, Sparkles, CalendarPlus, Trash2, Pencil } from "lucide-react";
import { ManualActivationModal } from "./ManualActivationModal";
import { ManualSubscriptionTransactionModal } from "./ManualSubscriptionTransactionModal";
import { CreateSubscriptionTransactionOnTypeChangeModal } from "./CreateSubscriptionTransactionOnTypeChangeModal";
import { EditSubscriptionTransactionModal } from "./EditSubscriptionTransactionModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { addMonths, differenceInDays, parseISO } from "date-fns";

interface TenantSubscriptionTabProps {
  tenant: {
    id: string;
    name: string;
    subscription_status: string | null;
    subscription_plan_id: string | null;
    subscription_type: string | null;
    subscription_payment_method: string | null;
    subscription_start_date: string | null;
    subscription_next_billing_date: string | null;
    subscription_last_payment_date: string | null;
  };
  onUpdate: () => void;
}

export function TenantSubscriptionTab({ tenant, onUpdate }: TenantSubscriptionTabProps) {
  const [manualActivationOpen, setManualActivationOpen] = useState(false);
  const [manualTransactionOpen, setManualTransactionOpen] = useState(false);
  const [createTransactionOnTypeChangeOpen, setCreateTransactionOnTypeChangeOpen] = useState(false);
  const [pendingTypeChange, setPendingTypeChange] = useState<string | null>(null);
  const [localTenant, setLocalTenant] = useState(tenant);
  const [editingTransaction, setEditingTransaction] = useState<any | null>(null);
  const [editTransactionModalOpen, setEditTransactionModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const queryClient = useQueryClient();
  
  // Função para calcular dias restantes baseado nas transações ativas do tenant
  const calculateDaysUntilExpiration = () => {
    if (!transactions || transactions.length === 0) {
      // Se não tem transações mas é tipo especial, considerar lifetime
      if (localTenant.subscription_type === 'cortesia' || localTenant.subscription_type === 'testers') {
        return null; // Lifetime
      }
      return null;
    }

    // Buscar transações ativas (não expiradas)
    const now = new Date();
    const activeTransactions = transactions.filter((t: any) => {
      if (t.status !== 'approved') return false;
      
      // Verificar se tem expiration_date
      if (t.expiration_date) {
        const expiration = parseISO(t.expiration_date);
        return expiration >= now;
      }
      
      // Se não tem expiration_date mas tem payment_date, verificar se é recente (últimos 30 dias)
      if (t.payment_date) {
        const payment = parseISO(t.payment_date);
        const daysSincePayment = differenceInDays(now, payment);
        return daysSincePayment <= 30; // Assumir 30 dias se não tiver expiration_date
      }
      
      return false;
    });

    // Verificar se há transação lifetime (sem expiration_date e status approved)
    const lifetimeTransaction = activeTransactions.find((t: any) => t.expiration_date === null && t.status === 'approved');
    if (lifetimeTransaction) {
      return null; // Lifetime
    }

    // Buscar transação com expiration_date mais distante
    const transactionsWithExpiration = activeTransactions
      .filter((t: any) => t.expiration_date !== null)
      .sort((a: any, b: any) => {
        const dateA = parseISO(a.expiration_date);
        const dateB = parseISO(b.expiration_date);
        return dateB.getTime() - dateA.getTime();
      });

    if (transactionsWithExpiration.length > 0) {
      const expirationDate = parseISO(transactionsWithExpiration[0].expiration_date);
      const days = differenceInDays(expirationDate, now);
      return days > 0 ? days : 0;
    }

    // Fallback: usar subscription_next_billing_date
    if (localTenant.subscription_next_billing_date) {
      const nextBilling = parseISO(localTenant.subscription_next_billing_date);
      const days = differenceInDays(nextBilling, now);
      return days > 0 ? days : 0;
    }

    // Se é tipo especial sem data definida, considerar lifetime
    if ((localTenant.subscription_type === 'cortesia' || localTenant.subscription_type === 'testers') && !localTenant.subscription_next_billing_date) {
      return null; // Lifetime
    }

    return null;
  };

  // Atualizar estado local quando o tenant prop mudar
  useEffect(() => {
    setLocalTenant(tenant);
  }, [tenant]);

  // Buscar todos os planos disponíveis
  const { data: availablePlans } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("id, name")
        .eq("status", "active")
        .order("price", { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
  });

  // Buscar plano atual
  const { data: currentPlan } = useQuery({
    queryKey: ["subscription-plan", localTenant.subscription_plan_id],
    queryFn: async () => {
      if (!localTenant.subscription_plan_id) return null;
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("name, id")
        .eq("id", localTenant.subscription_plan_id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!localTenant.subscription_plan_id,
  });

  const { data: transactions, isLoading } = useQuery({
    queryKey: ["subscription-transactions", localTenant.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_transactions")
        .select("*")
        .eq("tenant_id", localTenant.id)
        .order("expiration_date", { ascending: false, nullsFirst: false })
        .order("payment_date", { ascending: false, nullsFirst: false });

      if (error) throw error;
      
      // Ordenar no cliente também para garantir ordem correta
      const sorted = (data || []).sort((a: any, b: any) => {
        // Lifetime (null) vem primeiro
        if (a.expiration_date === null && b.expiration_date !== null) return -1;
        if (a.expiration_date !== null && b.expiration_date === null) return 1;
        
        // Se ambas têm expiration_date, ordenar por data mais distante
        if (a.expiration_date && b.expiration_date) {
          const dateA = new Date(a.expiration_date).getTime();
          const dateB = new Date(b.expiration_date).getTime();
          return dateB - dateA;
        }
        
        // Fallback: ordenar por payment_date
        if (a.payment_date && b.payment_date) {
          const dateA = new Date(a.payment_date).getTime();
          const dateB = new Date(b.payment_date).getTime();
          return dateB - dateA;
        }
        
        // Fallback final: ordenar por created_at
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });
      
      return sorted;
    },
  });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = (transactions || []).slice(startIndex, endIndex);
  const totalPages = Math.ceil((transactions?.length || 0) / itemsPerPage);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("pt-BR");
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      trial: "Trial",
      active: "Ativo",
      suspended: "Suspenso",
      cancelled: "Cancelado",
    };
    return labels[status] || status;
  };

  const getStatusBadge = (status: string) => {
    // Retornar variant e classe CSS customizada
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; className: string }> = {
      trial: { variant: "secondary", className: "bg-gray-500 hover:bg-gray-600 text-white" },
      active: { variant: "default", className: "bg-green-600 hover:bg-green-700 text-white" },
      suspended: { variant: "destructive", className: "bg-red-600 hover:bg-red-700 text-white" },
      cancelled: { variant: "outline", className: "bg-gray-400 hover:bg-gray-500 text-white border-gray-400" },
    };
    return variants[status] || { variant: "outline", className: "" };
  };

  const getPlanBadge = (plan: string) => {
    return plan === "plus" ? "default" : "secondary";
  };

  const getTransactionStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      approved: "Aprovado",
      pending: "Pendente",
      rejected: "Rejeitado",
      cancelled: "Cancelado",
    };
    return labels[status] || status;
  };

  const getTransactionStatusBadge = (status: string) => {
    // Aprovado deve ser verde
    if (status === "approved") {
      return "default" as const; // Vamos usar className customizado para verde
    }
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      rejected: "destructive",
      cancelled: "outline",
    };
    return variants[status] || "outline";
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      payment: "Pagamento",
      type_change: "Mudança de Tipo",
    };
    return labels[type] || type;
  };

  const getPaymentTypeBadge = (paymentType: string | null) => {
    if (!paymentType || paymentType === 'regular') return null;
    const variants: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
      cortesia: { label: "Cortesia", variant: "secondary" },
      testers: { label: "Testers", variant: "outline" },
      promotion: { label: "Promoção", variant: "default" },
    };
    return variants[paymentType] || null;
  };

  // Mutation para atualizar o plano do tenant
  const updatePlanMutation = useMutation({
    mutationFn: async (newPlanId: string | null) => {
      console.log("updatePlanMutation: Atualizando plano para", newPlanId);
      
      const updateData: any = {
        subscription_plan_id: newPlanId || null,
        subscription_status: "active", // Garantir que fique ativo sempre
      };

      // Atualizar tenant
      const { error: tenantError, data: updatedTenant } = await supabase
        .from("tenants")
        .update(updateData)
        .eq("id", localTenant.id)
        .select()
        .single();

      if (tenantError) {
        console.error("updatePlanMutation: Erro ao atualizar tenant:", tenantError);
        throw tenantError;
      }

      // Verificar o que foi realmente atualizado no banco
      const { data: verifiedTenant, error: fetchError } = await supabase
        .from("tenants")
        .select("subscription_plan_id")
        .eq("id", localTenant.id)
        .single();

      if (fetchError) {
        console.error("updatePlanMutation: Erro ao verificar tenant atualizado:", fetchError);
      } else if (verifiedTenant) {
        console.log("updatePlanMutation: Tenant atualizado no banco:", verifiedTenant);
        
        // Validar se o plano foi atualizado corretamente
        if (verifiedTenant.subscription_plan_id !== newPlanId) {
          console.error("updatePlanMutation: ERRO - subscription_plan_id deveria ser", newPlanId, "mas é", verifiedTenant.subscription_plan_id);
        }
      }

      return updatedTenant;
    },
    onSuccess: (updatedTenant) => {
      console.log("updatePlanMutation: Sucesso, atualizando estado local com:", updatedTenant);
      setLocalTenant(updatedTenant);
      queryClient.invalidateQueries({ queryKey: ["tenant", localTenant.id] });
      queryClient.invalidateQueries({ queryKey: ["subscription-plan", updatedTenant.subscription_plan_id] });
      toast.success("Plano atualizado com sucesso!");
    },
    onError: (error: any) => {
      console.error("updatePlanMutation: Erro ao atualizar plano:", error);
      toast.error("Erro ao atualizar plano: " + error.message);
    },
  });

  const handlePlanChange = (newPlanId: string) => {
    const currentPlanId = localTenant.subscription_plan_id || null;
    
    console.log("handlePlanChange: Mudando de", currentPlanId, "para", newPlanId);
    
    // Se não houve mudança, não fazer nada
    if (newPlanId === currentPlanId) {
      console.log("handlePlanChange: Sem mudança, ignorando");
      return;
    }

    // Converter string vazia para null
    const planIdToUpdate = newPlanId === "trial" || newPlanId === "" ? null : newPlanId;
    
    updatePlanMutation.mutate(planIdToUpdate);
  };

  const handleSubscriptionTypeChange = (newType: string) => {
    const currentType = localTenant.subscription_type || 'regular';
    
    console.log("handleSubscriptionTypeChange: Mudando de", currentType, "para", newType);
    
    // Se não houve mudança, não fazer nada
    if (newType === currentType) {
      console.log("handleSubscriptionTypeChange: Sem mudança, ignorando");
      return;
    }

    // Se mudando para tipo especial (cortesia/testers/promotion), abrir modal para criar transação
    if (newType === 'cortesia' || newType === 'testers' || newType === 'promotion') {
      console.log("handleSubscriptionTypeChange: Abrindo modal para criar transação para tipo", newType);
      setPendingTypeChange(newType);
      setCreateTransactionOnTypeChangeOpen(true);
    } else {
      // Se mudando para regular, atualizar diretamente sem criar transação
      console.log("handleSubscriptionTypeChange: Atualizando diretamente para regular");
      updateSubscriptionTypeMutation.mutate(newType);
    }
  };

  const updateSubscriptionTypeMutation = useMutation({
    mutationFn: async (newType: string) => {
      const currentType = localTenant.subscription_type || 'regular';
      
      console.log("updateSubscriptionTypeMutation: Atualizando de", currentType, "para", newType);
      
      // Atualizar apenas o subscription_type do tenant
      const updateData: any = {
        subscription_type: newType,
        subscription_status: "active", // Garantir que fique ativo sempre
      };

      // Atualizar método de pagamento baseado no novo tipo
      if (newType === 'regular') {
        // Se mudando para regular de um tipo especial, mudar método para manual
        if (currentType === 'cortesia' || currentType === 'testers' || currentType === 'promotion') {
          updateData.subscription_payment_method = 'manual';
        }
      } else if (newType === 'cortesia') {
        updateData.subscription_payment_method = 'cortesia';
      } else if (newType === 'testers') {
        updateData.subscription_payment_method = 'testers';
      } else if (newType === 'promotion') {
        updateData.subscription_payment_method = 'promotion';
      }

      console.log("updateSubscriptionTypeMutation: UpdateData:", updateData);

      // Fazer UPDATE e verificar a resposta
      const { data: updateResponse, error: tenantError } = await supabase
        .from("tenants")
        .update(updateData)
        .eq("id", localTenant.id)
        .select("subscription_type, subscription_payment_method, subscription_status")
        .single();

      console.log("updateSubscriptionTypeMutation: Resposta do UPDATE:", updateResponse, "Erro:", tenantError);

      if (tenantError) {
        console.error("updateSubscriptionTypeMutation: Erro ao atualizar:", tenantError);
        throw tenantError;
      }

      if (!updateResponse) {
        console.error("updateSubscriptionTypeMutation: UPDATE retornou vazio - possível bloqueio RLS");
        throw new Error("UPDATE não retornou dados. Possível problema de RLS ou permissões.");
      }

      // Aguardar um pouco para garantir que o UPDATE foi persistido
      await new Promise(resolve => setTimeout(resolve, 100));

      // Verificar o que foi realmente atualizado no banco
      const { data: updatedTenant, error: fetchError } = await supabase
        .from("tenants")
        .select("subscription_type, subscription_payment_method, subscription_status")
        .eq("id", localTenant.id)
        .single();

      if (fetchError) {
        console.error("updateSubscriptionTypeMutation: Erro ao buscar tenant após UPDATE:", fetchError);
      } else if (updatedTenant) {
        console.log("updateSubscriptionTypeMutation: Tenant buscado após UPDATE:", updatedTenant);
        
        // Validar se o tipo foi atualizado corretamente
        if (updatedTenant.subscription_type !== newType) {
          console.error("updateSubscriptionTypeMutation: ERRO - subscription_type não foi atualizado!", {
            esperado: newType,
            retornadoPeloUPDATE: updateResponse?.subscription_type,
            retornadoPelaQuery: updatedTenant.subscription_type
          });
          throw new Error(`Erro: subscription_type não foi atualizado corretamente. Esperado: ${newType}, Atual no banco: ${updatedTenant.subscription_type}`);
        }
      }

      // Retornar dados atualizados do banco (não os que enviamos)
      return { ...localTenant, ...(updateResponse || updatedTenant || updateData) };
    },
    onSuccess: (updatedTenant) => {
      toast.success("Tipo de assinatura atualizado com sucesso!");
      // Atualizar estado local com os dados atualizados
      if (updatedTenant) {
        setLocalTenant(updatedTenant);
        // Invalidar queries para garantir sincronização
        queryClient.invalidateQueries({ queryKey: ["tenants"] });
        queryClient.invalidateQueries({ queryKey: ["subscription-transactions", updatedTenant.id] });
        queryClient.invalidateQueries({ queryKey: ["active-subscription-transactions", updatedTenant.id] });
      } else {
        // Se não retornou dados atualizados, apenas invalidar queries
        queryClient.invalidateQueries({ queryKey: ["tenants"] });
        queryClient.invalidateQueries({ queryKey: ["subscription-transactions", localTenant.id] });
        queryClient.invalidateQueries({ queryKey: ["active-subscription-transactions", localTenant.id] });
      }
      // NÃO chamar onUpdate() para não fechar o modal
    },
    onError: (error: any) => {
      toast.error("Erro ao atualizar tipo: " + error.message);
    },
  });

  const handleCreateTransactionOnTypeChangeSuccess = async () => {
    // Após criar transação, atualizar tipo do tenant se ainda não foi atualizado
    if (pendingTypeChange) {
      // A transação já atualiza o tenant, apenas buscar dados atualizados
      const { data: updatedTenant, error } = await supabase
        .from("tenants")
        .select("*")
        .eq("id", localTenant.id)
        .single();
      
      if (!error && updatedTenant) {
        setLocalTenant(updatedTenant);
        queryClient.invalidateQueries({ queryKey: ["tenants"] });
        queryClient.invalidateQueries({ queryKey: ["subscription-transactions", localTenant.id] });
        queryClient.invalidateQueries({ queryKey: ["active-subscription-transactions", localTenant.id] });
      }
      setPendingTypeChange(null);
    }
    setCreateTransactionOnTypeChangeOpen(false);
  };

  const handleManualActivationSuccess = async () => {
    // Buscar dados atualizados do tenant
    const { data: updatedTenant, error } = await supabase
      .from("tenants")
      .select("*")
      .eq("id", localTenant.id)
      .single();

    if (!error && updatedTenant) {
      setLocalTenant({
        id: updatedTenant.id,
        name: updatedTenant.name,
        subscription_status: updatedTenant.subscription_status,
        subscription_plan_id: updatedTenant.subscription_plan_id,
        subscription_type: updatedTenant.subscription_type,
        subscription_payment_method: updatedTenant.subscription_payment_method,
        subscription_start_date: updatedTenant.subscription_start_date,
        subscription_next_billing_date: updatedTenant.subscription_next_billing_date,
        subscription_last_payment_date: updatedTenant.subscription_last_payment_date,
      });
    }

    queryClient.invalidateQueries({ queryKey: ["tenants"] });
    queryClient.invalidateQueries({ queryKey: ["subscription-transactions", localTenant.id] });
    setManualActivationOpen(false);
    setManualTransactionOpen(false);
    // Não fechar o modal, apenas atualizar estado
  };

  const deleteTransactionMutation = useMutation({
    mutationFn: async (transactionId: string) => {
      // Buscar a transação que será excluída
      const { data: transactionToDelete, error: fetchError } = await supabase
        .from("subscription_transactions")
        .select("*")
        .eq("id", transactionId)
        .single();

      if (fetchError) throw fetchError;
      if (!transactionToDelete) throw new Error("Transação não encontrada");

      // Excluir a transação
      const { error: deleteError } = await supabase
        .from("subscription_transactions")
        .delete()
        .eq("id", transactionId);

      if (deleteError) throw deleteError;

      // Buscar todas as transações restantes aprovadas
      const { data: remainingTransactions, error: remainingError } = await supabase
        .from("subscription_transactions")
        .select("*")
        .eq("tenant_id", localTenant.id)
        .eq("status", "approved")
        .order("payment_date", { ascending: false });

      if (remainingError) throw remainingError;

      console.log("DeleteTransaction: Transações restantes:", remainingTransactions?.length || 0, remainingTransactions);

      // Atualizar tenant baseado nas transações restantes
      const updateData: any = {};
      let newSubscriptionType: string = "regular";
      let newPaymentMethod: string | null = "manual";

      // Verificar se há transações restantes
      if (!remainingTransactions || remainingTransactions.length === 0) {
        // NÃO HÁ TRANSAÇÕES RESTANTES - atualizar para regular
        console.log("DeleteTransaction: Nenhuma transação restante - atualizando para regular");
        updateData.subscription_last_payment_date = null;
        updateData.subscription_next_billing_date = null;
        updateData.subscription_type = "regular";
        updateData.subscription_payment_method = "manual";
        
        // Se tem plano definido, manter ativo; caso contrário, pode inativar
        if (localTenant.subscription_plan_id) {
          updateData.subscription_status = "active";
        } else {
          updateData.subscription_status = "inactive";
        }
      } else {
        // Há transações restantes - filtrar apenas as válidas (não expiradas ou lifetime)
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        
        const validTransactions = remainingTransactions.filter((t: any) => {
          // Lifetime (expiration_date null) sempre é válido
          if (!t.expiration_date) return true;
          // Verificar se expiration_date é no futuro ou hoje
          const expirationDate = new Date(t.expiration_date);
          expirationDate.setHours(0, 0, 0, 0);
          return expirationDate >= now;
        });

        console.log("DeleteTransaction: Transações válidas:", validTransactions.length, validTransactions);

        if (validTransactions.length > 0) {
          // Há transações válidas - usar a última válida
          // Ordenar transações válidas: lifetime primeiro, depois por expiration_date desc, depois por payment_date desc
          const sortedValidTransactions = validTransactions.sort((a: any, b: any) => {
            // Lifetime tem prioridade
            if (!a.expiration_date && b.expiration_date) return -1;
            if (a.expiration_date && !b.expiration_date) return 1;
            
            // Se ambas têm expiration_date, ordenar por expiration_date desc
            if (a.expiration_date && b.expiration_date) {
              const dateA = new Date(a.expiration_date).getTime();
              const dateB = new Date(b.expiration_date).getTime();
              if (dateA !== dateB) return dateB - dateA;
            }
            
            // Se expiration_date for igual ou ambas lifetime, ordenar por payment_date desc
            const paymentDateA = new Date(a.payment_date).getTime();
            const paymentDateB = new Date(b.payment_date).getTime();
            return paymentDateB - paymentDateA;
          });

          // Pegar a última transação válida (primeira da lista ordenada)
          const lastValidTransaction = sortedValidTransactions[0];
          console.log("DeleteTransaction: Última transação válida:", lastValidTransaction);
          
          // Atualizar subscription_type baseado no payment_type da última transação válida
          newSubscriptionType = lastValidTransaction.payment_type || "regular";
          
          // Definir payment_method baseado no tipo
          if (newSubscriptionType === "regular") {
            newPaymentMethod = "manual";
          } else if (newSubscriptionType === "cortesia") {
            newPaymentMethod = "cortesia";
          } else if (newSubscriptionType === "testers") {
            newPaymentMethod = "testers";
          } else if (newSubscriptionType === "promotion") {
            newPaymentMethod = "promotion";
          } else {
            newPaymentMethod = "manual";
          }

          // Calcular datas de pagamento e próxima cobrança
          if (lastValidTransaction.expiration_date) {
            // Transação com expiração
            const expirationDate = new Date(lastValidTransaction.expiration_date);
            updateData.subscription_next_billing_date = expirationDate.toISOString();
            updateData.subscription_last_payment_date = lastValidTransaction.payment_date;
          } else {
            // Lifetime - não definir próxima cobrança
            updateData.subscription_next_billing_date = null;
            updateData.subscription_last_payment_date = lastValidTransaction.payment_date;
          }
        } else {
          // TODAS AS TRANSAÇÕES RESTANTES ESTÃO EXPIRADAS - atualizar para regular
          console.log("DeleteTransaction: Todas as transações restantes estão expiradas - atualizando para regular");
          updateData.subscription_last_payment_date = null;
          updateData.subscription_next_billing_date = null;
          updateData.subscription_type = "regular";
          updateData.subscription_payment_method = "manual";
          
          // Se tem plano definido, manter ativo; caso contrário, pode inativar
          if (localTenant.subscription_plan_id) {
            updateData.subscription_status = "active";
          } else {
            updateData.subscription_status = "inactive";
          }
        }
        
        // Só atualizar status e tipo se há transações válidas
        if (validTransactions.length > 0) {
          updateData.subscription_status = "active";
          updateData.subscription_type = newSubscriptionType;
          updateData.subscription_payment_method = newPaymentMethod;
        }
      }

      console.log("DeleteTransaction: UpdateData:", updateData);

      // Atualizar tenant com o novo subscription_type baseado na última transação válida
      const { error: tenantError } = await supabase
        .from("tenants")
        .update(updateData)
        .eq("id", localTenant.id);

      if (tenantError) {
        console.error("DeleteTransaction: Erro ao atualizar tenant:", tenantError);
        throw tenantError;
      }

      // Verificar o que foi realmente atualizado no banco
      const { data: updatedTenantAfterDelete, error: fetchTenantError } = await supabase
        .from("tenants")
        .select("subscription_type, subscription_payment_method, subscription_status, subscription_last_payment_date, subscription_next_billing_date")
        .eq("id", localTenant.id)
        .single();

      if (fetchTenantError) {
        console.error("DeleteTransaction: Erro ao buscar tenant atualizado:", fetchTenantError);
      } else if (updatedTenantAfterDelete) {
        console.log("DeleteTransaction: Tenant atualizado no banco:", updatedTenantAfterDelete);
        
        // Validar se o tipo foi atualizado corretamente se deveria ser regular
        if (updateData.subscription_type === "regular" && updatedTenantAfterDelete.subscription_type !== "regular") {
          console.error("DeleteTransaction: ERRO - subscription_type deveria ser regular mas é", updatedTenantAfterDelete.subscription_type);
        }
      }

      return transactionToDelete;
    },
    onSuccess: async () => {
      toast.success("Lançamento excluído com sucesso!");
      // Buscar dados atualizados do tenant do banco
      const { data: updatedTenant, error } = await supabase
        .from("tenants")
        .select("*")
        .eq("id", localTenant.id)
        .single();
      
      if (!error && updatedTenant) {
        console.log("DeleteTransaction: Atualizando estado local com:", updatedTenant);
        setLocalTenant(updatedTenant);
        // Invalidar queries para garantir sincronização
        queryClient.invalidateQueries({ queryKey: ["tenants"] });
        queryClient.invalidateQueries({ queryKey: ["subscription-transactions", localTenant.id] });
        queryClient.invalidateQueries({ queryKey: ["active-subscription-transactions", localTenant.id] });
      } else {
        // Fallback: usar handleManualActivationSuccess
        handleManualActivationSuccess();
      }
    },
    onError: (error: any) => {
      toast.error("Erro ao excluir lançamento: " + error.message);
    },
  });

  const handleDeleteTransaction = (transactionId: string) => {
    if (window.confirm("Tem certeza que deseja excluir este lançamento? Esta ação não pode ser desfeita.")) {
      deleteTransactionMutation.mutate(transactionId);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Status da Assinatura</CardTitle>
          <CardDescription>Informações sobre o plano e pagamento</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pb-2">
          {/* Primeira linha: Status, Plano, Dias Restantes, Tipo */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Status</p>
              {(() => {
                const statusBadge = getStatusBadge(localTenant.subscription_status || "trial");
                return (
                  <Badge variant={statusBadge.variant} className={`mt-1 ${statusBadge.className}`}>
                    {getStatusLabel(localTenant.subscription_status || "trial")}
                  </Badge>
                );
              })()}
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Plano</p>
              <div className="mt-1">
                <Select
                  value={localTenant.subscription_plan_id || "trial"}
                  onValueChange={handlePlanChange}
                  disabled={updatePlanMutation.isPending}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue>
                      {currentPlan?.name || localTenant.subscription_plan_id || "Trial"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    {availablePlans?.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {updatePlanMutation.isPending && (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                    <span className="text-xs text-muted-foreground">Atualizando...</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Dias Restantes</p>
              <p className="text-sm mt-1 font-semibold">
                {(() => {
                  const daysUntilExpiration = calculateDaysUntilExpiration();
                  if (daysUntilExpiration === null) {
                    return <span className="text-green-600">Para sempre</span>;
                  }
                  if (daysUntilExpiration <= 0) {
                    return <span className="text-red-600">Expirado</span>;
                  }
                  if (daysUntilExpiration <= 7) {
                    return <span className="text-amber-600">{daysUntilExpiration} {daysUntilExpiration === 1 ? 'dia' : 'dias'}</span>;
                  }
                  return <span className="text-green-600">{daysUntilExpiration} {daysUntilExpiration === 1 ? 'dia' : 'dias'}</span>;
                })()}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Tipo</p>
              <div className="flex items-center gap-2 mt-1">
                <Select
                  value={localTenant.subscription_type || "regular"}
                  onValueChange={handleSubscriptionTypeChange}
                  disabled={updateSubscriptionTypeMutation.isPending}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="cortesia">Cortesia</SelectItem>
                    <SelectItem value="testers">Beta Tester</SelectItem>
                    <SelectItem value="promotion">Promoção</SelectItem>
                  </SelectContent>
                </Select>
                {updateSubscriptionTypeMutation.isPending && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Altere o tipo a qualquer momento
              </p>
            </div>
          </div>

          {/* Segunda linha: Método de Pagamento, Data de Início, Próxima Cobrança, Último Pagamento */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Método de Pagamento</p>
              <p className="text-sm mt-1">
                {localTenant.subscription_type === 'cortesia' || 
                 localTenant.subscription_type === 'testers' || 
                 localTenant.subscription_type === 'promotion'
                  ? 'Sem cobrança'
                  : localTenant.subscription_payment_method || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Data de Início</p>
              <p className="text-sm mt-1">{formatDate(localTenant.subscription_start_date)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Próxima Cobrança</p>
              <p className="text-sm mt-1">{formatDate(localTenant.subscription_next_billing_date)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Último Pagamento</p>
              <p className="text-sm mt-1">{formatDate(localTenant.subscription_last_payment_date)}</p>
            </div>
          </div>

          {localTenant.subscription_type === 'cortesia' && (
            <div className="rounded-lg border-2 border-amber-700 bg-amber-50/80 p-3 flex items-start gap-4 w-full">
              <CheckCircle className="h-6 w-6 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-lg font-semibold text-amber-900">
                  {currentPlan?.name || localTenant.subscription_plan_id || "Trial"} Ativo • Cortesia
                </p>
                <p className="text-sm text-amber-700/80 mt-1">
                  Este tenant possui um plano de cortesia e não será cobrado.
                </p>
              </div>
            </div>
          )}

          {localTenant.subscription_type === 'testers' && (
            <div className="rounded-lg border-2 border-blue-700 bg-blue-50/80 p-3 flex items-start gap-4 w-full">
              <CheckCircle className="h-6 w-6 text-blue-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-lg font-semibold text-blue-900">
                  {currentPlan?.name || localTenant.subscription_plan_id || "Trial"} Ativo • Beta Tester
                </p>
                <p className="text-sm text-blue-700/80 mt-1">
                  Este tenant possui um Plano Beta Tester e não será cobrado.
                </p>
              </div>
            </div>
          )}

          {localTenant.subscription_type === 'promotion' && (
            <div className="rounded-lg border-2 border-purple-700 bg-purple-50/80 p-3 flex items-start gap-4 w-full">
              <CheckCircle className="h-6 w-6 text-purple-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-lg font-semibold text-purple-900">
                  {currentPlan?.name || localTenant.subscription_plan_id || "Trial"} Ativo • Promoção
                </p>
                <p className="text-sm text-purple-700/80 mt-1">
                  Este tenant possui um plano promocional e não será cobrado.
                </p>
              </div>
            </div>
          )}

          {(!localTenant.subscription_type || localTenant.subscription_type === 'regular') && localTenant.subscription_status === 'active' && (
            <div className="rounded-lg border-2 border-emerald-700 bg-emerald-50/80 p-3 flex items-start gap-4 w-full">
              <CheckCircle className="h-6 w-6 text-emerald-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-lg font-semibold text-emerald-900">
                  {currentPlan?.name || localTenant.subscription_plan_id || "Trial"} Ativo • Regular
                </p>
                <p className="text-sm text-emerald-700/80 mt-1">
                  Este tenant possui um plano regular ativo.
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {/* Mostrar botão de ativar plano apenas se não estiver ativo ou se não tiver plano */}
            {(!localTenant.subscription_plan_id || localTenant.subscription_status !== "active") && (
              <Button
                onClick={() => setManualActivationOpen(true)}
                className="w-full"
              >
                Ativar Plano Manualmente
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <CardTitle>Histórico de Transações</CardTitle>
              <CardDescription>
                {transactions?.length || 0} transações registradas
              </CardDescription>
            </div>
            {/* Mostrar botão de lançar transações apenas para tipo regular */}
            {(!localTenant.subscription_type || localTenant.subscription_type === 'regular') && (
              <Button
                onClick={() => setManualTransactionOpen(true)}
                variant="outline"
                className="flex-shrink-0"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Lançar Transações de Assinatura
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : transactions && transactions.length > 0 ? (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Tipo</TableHead>
                    <TableHead>Tipo Pag./Método</TableHead>
                    <TableHead>Valor / Status / Observações</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTransactions.map((transaction: any, index: number) => {
                    const paymentTypeBadge = getPaymentTypeBadge(transaction.payment_type);
                    const globalIndex = startIndex + index;
                    const isMostRecent = globalIndex === 0;
                    
                    // Verificar se ainda não venceu usando expiration_date se disponível, senão payment_date
                    const expirationDate = transaction.expiration_date ? new Date(transaction.expiration_date) : null;
                    const paymentDate = transaction.payment_date ? new Date(transaction.payment_date) : null;
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    
                    const checkDate = expirationDate || paymentDate;
                    const checkDateOnly = checkDate ? new Date(checkDate) : null;
                    if (checkDateOnly) {
                      checkDateOnly.setHours(0, 0, 0, 0);
                    }
                    const hasNotExpired = checkDateOnly ? checkDateOnly >= now : false;
                    
                    // Verificar se pode editar: transação aprovada e não expirada (ativa/válida ou futura)
                    const canEdit = transaction.status === "approved" && hasNotExpired && (transaction.expiration_date || transaction.payment_date);
                    
                    // Mostrar botão de excluir apenas se for a mais recente E ainda não venceu
                    const canDelete = isMostRecent && hasNotExpired && (transaction.expiration_date || transaction.payment_date);
                    
                    // Obter observações do metadata
                    const observations = transaction.metadata?.observations || transaction.metadata?.note || "-";
                    
                    return (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div>{formatDate(transaction.created_at)}</div>
                            <div className="text-xs text-muted-foreground">{getTransactionTypeLabel(transaction.transaction_type)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {paymentTypeBadge ? (
                              <Badge variant={paymentTypeBadge.variant}>
                                {paymentTypeBadge.label}
                              </Badge>
                            ) : (
                              "-"
                            )}
                            {/* Mostrar método apenas se não for cortesia ou testers */}
                            {transaction.payment_method && 
                             transaction.payment_type !== 'cortesia' && 
                             transaction.payment_type !== 'testers' && (
                              <div className="text-xs text-muted-foreground">
                                {transaction.payment_method}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {(transaction.amount && Number(transaction.amount) > 0) ? (
                                <>
                                  <span>{formatCurrency(transaction.amount)}</span>
                                  <span className="text-muted-foreground">•</span>
                                  <Badge 
                                    variant={getTransactionStatusBadge(transaction.status)}
                                    className={transaction.status === "approved" ? "bg-green-600 hover:bg-green-700 text-white" : ""}
                                  >
                                    {getTransactionStatusLabel(transaction.status)}
                                  </Badge>
                                </>
                              ) : (
                                <Badge 
                                  variant={getTransactionStatusBadge(transaction.status)}
                                  className={transaction.status === "approved" ? "bg-green-600 hover:bg-green-700 text-white" : ""}
                                >
                                  {getTransactionStatusLabel(transaction.status)}
                                </Badge>
                              )}
                            </div>
                            {transaction.metadata?.coupon_code && (
                              <div className="text-xs text-muted-foreground">
                                Cupom: {transaction.metadata.coupon_code}
                                {typeof transaction.metadata.discount_value !== 'undefined' && (
                                  <> • Desconto: {transaction.metadata.discount_type === 'percentage' ? `${transaction.metadata.discount_value}%` : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(transaction.metadata.discount_value || 0)}</>
                                )}
                                {typeof transaction.metadata.discounted_amount !== 'undefined' && (
                                  <> • Total: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(transaction.metadata.discounted_amount || 0)}</>
                                )}
                              </div>
                            )}
                            {observations !== "-" && (
                              <div className="text-xs text-muted-foreground truncate" title={observations}>
                                {observations}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingTransaction(transaction);
                                  setEditTransactionModalOpen(true);
                                }}
                                className="h-8 w-8 p-0"
                                title="Editar transação"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTransaction(transaction.id)}
                                disabled={deleteTransactionMutation.isPending}
                                className="h-8 w-8 p-0"
                                title="Excluir transação"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                            {!canEdit && !canDelete && (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma transação registrada
            </div>
          )}
        </CardContent>
        {transactions && transactions.length > 0 ? (
          <div className="border-t">
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages || 1}
              itemsPerPage={itemsPerPage}
              totalItems={transactions?.length || 0}
              onPageChange={(p) => setCurrentPage(p)}
              onItemsPerPageChange={(v) => {
                setItemsPerPage(v);
                setCurrentPage(1);
              }}
            />
          </div>
        ) : null}
      </Card>

      <ManualActivationModal
        open={manualActivationOpen}
        onOpenChange={setManualActivationOpen}
        tenantId={localTenant.id}
        tenantName={localTenant.name}
        onSuccess={handleManualActivationSuccess}
      />

      <ManualSubscriptionTransactionModal
        open={manualTransactionOpen}
        onOpenChange={setManualTransactionOpen}
        tenantId={localTenant.id}
        tenantName={localTenant.name}
        currentNextBillingDate={localTenant.subscription_next_billing_date}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["subscription-transactions", localTenant.id] });
          queryClient.invalidateQueries({ queryKey: ["active-subscription-transactions", localTenant.id] });
          handleManualActivationSuccess();
        }}
      />

      {pendingTypeChange && (
        <CreateSubscriptionTransactionOnTypeChangeModal
          open={createTransactionOnTypeChangeOpen}
          onOpenChange={setCreateTransactionOnTypeChangeOpen}
          tenantId={localTenant.id}
          tenantName={localTenant.name}
          subscriptionType={pendingTypeChange}
          onSuccess={handleCreateTransactionOnTypeChangeSuccess}
        />
      )}

      <EditSubscriptionTransactionModal
        open={editTransactionModalOpen}
        onOpenChange={setEditTransactionModalOpen}
        transaction={editingTransaction}
        tenantId={localTenant.id}
        tenantName={localTenant.name}
        onSuccess={() => {
          // Recarregar dados do tenant e transações
          queryClient.invalidateQueries({ queryKey: ["tenant-subscription-transactions", localTenant.id] });
          queryClient.invalidateQueries({ queryKey: ["tenant", localTenant.id] });
          onUpdate();
        }}
      />
    </div>
  );
}
